import { BASIC_AUTH_HEADER, BASIC_AUTH_QUERY_PARAM } from "@/types/auth"
import { getSessionId } from "@amplitude/analytics-browser"
import {
  AxiosInstance,
  CreateAxiosDefaults,
  RawAxiosRequestHeaders,
} from "axios"

import { APP_INSTANCE_ID } from "@/lib/app-instance"
import { addEmbeddedAuthHeader } from "@/lib/auth-utils"
import { apiBaseUrl, axiosInstance } from "@/lib/axios-utils"
import { getCurrentPosition } from "@/lib/current-position"
import { nowMillis } from "@/lib/date-utils"
import {
  embeddedOrgid,
  embeddedSourceId,
  embeddedSourceUrl,
  isEmbedded,
} from "@/lib/is-embedded"
import { getLocaleHeaders } from "@/lib/locale-headers"
import { isDevelopment } from "@/lib/utils/misc"
import { installVoiceHttpTrace } from "@/lib/voice-trace/http-trace"
import { isClientSide } from "./is-client-side"
import { mtLocation } from "./mt-location"

/**
 * This function builds an Axios instance intended for client-side requests. It uses the "withCredentials" option to
 * send cookies with the request. This will not work server-side as those cookies are not automatically sent with the
 * request. For server-side requests, use `buildServerAxios`.
 *
 * @returns {AxiosInstance} The configured Axios instance for client-side requests.
 */
export function buildClientAxios(
  type: "rails" | "nextjs" = "rails",
  options?: { retries?: number; adapter?: "fetch" | "http" }
): AxiosInstance {
  const headers: RawAxiosRequestHeaders = {
    "Content-Type": "application/json",
    "X-Mindtrip-Embedded": isEmbedded(),
    "X-Mindtrip-Embedded-Orgid": embeddedOrgid(),
    "X-Mindtrip-Embedded-Source-Url": embeddedSourceUrl(),
    "X-Mindtrip-Source-Id": embeddedSourceId(),
    "X-Mindtrip-App-Instance": APP_INSTANCE_ID,
  }

  // forward browser headers similar to server-side forwarding
  // todo this does not work because we only allow mt-* headers to come from the server
  // to prevent spoofing
  // if (isClientSide()) {
  //   // Forward referrer for search engine redirect logic
  //   if (document.referrer) {
  //     headers["mt-forwarded-referer"] = document.referrer
  //   }
  //
  //   // Forward user agent (equivalent to server-side user-agent forwarding)
  //   if (navigator.userAgent) {
  //     headers["mt-forwarded-user-agent"] = navigator.userAgent
  //   }
  //
  //   // Forward client IP information if available (from browser perspective)
  //   if (window.location.hostname) {
  //     headers["mt-forwarded-host"] = window.location.hostname
  //   }
  // }

  const searchParams = new URLSearchParams(
    isClientSide() ? (mtLocation.search ?? "") : ""
  )

  const basicAuthQueryParam = searchParams.get(BASIC_AUTH_QUERY_PARAM)
  if (basicAuthQueryParam) {
    headers[BASIC_AUTH_HEADER] = basicAuthQueryParam
  }

  const axiosOptions: CreateAxiosDefaults = {
    withCredentials: true,
    headers,
  }

  if (type !== "nextjs") {
    // if type is next.js don't set the baseURL
    axiosOptions.baseURL = apiBaseUrl()
  }

  if (options?.adapter) {
    axiosOptions.adapter = options.adapter
  }

  const retries = options?.retries ?? 4
  const instance = axiosInstance(axiosOptions, retries, 700)

  // these headers can change, need to set just before request
  instance.interceptors.request.use((config) => {
    if (isEmbedded()) {
      addEmbeddedAuthHeader(config.headers)
    }

    // Add user location header if available (ambient context for backend)
    // latitude,longitude,accuracy_meters,age_seconds: the age lets the backend
    // tell a location fix restored or held from before a trip from a current one.
    const position = getCurrentPosition()
    if (position) {
      const ageSeconds =
        position.timestamp === undefined
          ? undefined
          : Math.max(0, Math.round((nowMillis() - position.timestamp) / 1000))
      const fields: (number | string)[] = [
        position.latitude,
        position.longitude,
      ]
      // An unknown accuracy stays an empty field when an age follows it.
      if (position.accuracy !== null || ageSeconds !== undefined) {
        fields.push(position.accuracy ?? "")
      }
      if (ageSeconds !== undefined) fields.push(ageSeconds)
      config.headers["User-Coordinates"] = fields.join(",")
    }

    const sessionId = getSessionId()
    if (sessionId) {
      config.headers["X-Amplitude-Session-Id"] = sessionId
    }

    if (isClientSide()) {
      Object.assign(config.headers, getLocaleHeaders())
    }

    return config
  })

  installVoiceHttpTrace(instance)

  // add an interceptor to look for 401 errors and redirect to login
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        isClientSide() &&
        isDevelopment() &&
        error.response.data &&
        typeof error.response.data === "string" &&
        error.response.data.includes("ActiveRecord::PendingMigrationError")
      ) {
        mtLocation.setHref("/pending-migrations")
      }

      // default error handling
      return Promise.reject(
        error instanceof Error ? error : new Error(`${error}`)
      )
    }
  )

  return instance
}
